<template>
  <t-rich-select v-model="selectedValue"
                 :value-attribute="valueAttribute"
                 :text-attribute="textAttribute"
                 :variant="variant"
                 :options="allAvailableOptions"
                 :data-cy="`queryable-select-${id}`"
                 no-results-text=""
                 :search-box-placeholder="searchBoxPlaceholder"
                 max-height="400"
                 ref="richSelect"
                 :disabled="isDisabled"
                 :class="isDisabled ? 'border-gray' : ''"
                 @keydown.down.native="handleKeyDown"
                 @focus="handleSelectFocus"
                 @blur="handleSelectBlur"
  >
    <template slot="option" slot-scope="{ option }">
      <div class="p-3">
        <div class="flex flex-col">
          <span class="text-base font-medium w-full"
                :class="isOptionActive(option.raw) ? 'text-primary-dark' : 'text-gray-dark'">
            <span v-if="option.raw && option.raw.isCustom" class="text-xs text-gray-500 mr-2">Create:</span>
            {{ option.raw[textAttribute] || option.raw[valueAttribute] }}
          </span>
          <span v-if="option.raw.subtitle" class="text-xs text-gray-dark mt-0.5">
            {{ option.raw.subtitle }}
          </span>
        </div>
      </div>
    </template>

    <template slot="label">
      <div class="min-w-0 flex-1 overflow-hidden flex flex-col leading-tight">
        <span class="truncate" :class="isDisabled ? 'text-gray' : 'text-black'">{{ selectedOption ? selectedOption[textAttribute] : inputText }}</span>
        <span v-if="selectedOption && selectedOption.subtitle"
              class="text-xs text-gray-dark truncate"
              data-testid="select-button-subtitle"
              :title="selectedOption.subtitle"
        >{{ selectedOption.subtitle }}</span>
      </div>
    </template>

    <template slot="noResults">
      <div class="p-3">
        {{ noResultsMessage }}
      </div>
    </template>

    <template slot="arrow">
      <sort-desc-icon class="transform transition-transform duration-500 text-primary background-primary"
                      :class="{ 'rotate-180': isDropdownOpen }"
                      order="desc"
                      :is-active="false"
      />
    </template>
  </t-rich-select>
</template>

<script>
import SortDescIcon from '@/components/common/SortableTableArrowsSort'
import clone from 'ramda/src/clone'
import prepend from 'ramda/src/prepend'
import isEmpty from 'ramda/src/isEmpty'
import filter from 'ramda/src/filter'
import prop from 'ramda/src/prop'
import propOr from 'ramda/src/propOr'
import any from 'ramda/src/any'
import equals from 'ramda/src/equals'
import not from 'ramda/src/not'
import both from 'ramda/src/both'
import concat from 'ramda/src/concat'
import sanitizeString from 'replace-special-characters'

export default {
  name: 'QueryableSelect',
  components: { SortDescIcon },
  props: {
    id: {
      type: String
    },
    label: {
      type: String,
      default: null
    },
    value: {
      type: String
    },
    options: {
      type: Array,
      required: true
    },
    valueAttribute: {
      type: String,
      default: 'value'
    },
    textAttribute: {
      type: String,
      default: 'text'
    },
    isDisabled: {
      type: Boolean,
      default: false
    },
    allowCustomOption: {
      type: Boolean,
      default: false
    },
    shouldFocusOnMount: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      inputText: '',
      variant: 'queryableSelect',
      isDropdownOpen: false,
      customOptions: []
    }
  },

  computed: {
    buttonWrapper() {
      return this.$refs.richSelect.$refs.buttonWrapper
    },
    selectedValue: {
      get() {
        return this.value
      },
      set(selectedValue) {
        this.handleValueSelection(selectedValue)
      }
    },
    allAvailableOptions() {
      return concat(this.options, this.customOptions)
    },
    selectedOption() {
      if (!this.selectedValue) return null
      return this.allAvailableOptions.find(opt => opt[this.valueAttribute] === this.selectedValue)
    },
    isCustomOptionShown() {
      return both(
        () => this.allowCustomOption,
        () => not(isEmpty(this.inputText))
      )() && not(this.hasMatchingOption())
    },
    noResultsMessage() {
      return this.label || this.id ?
        `The ${this.label || this.id} you are searching for does not exist!` :
        'No matching results found.'
    },
    searchBoxPlaceholder() {
      return this.allowCustomOption ? 'Search or create new' : 'Search...'
    }
  },

  watch: {
    inputText(value) {
      value ? this.showDropdown() : this.hideDropDown()
    }
  },

  methods: {
    hideDropDown() {
      this.variant = 'queryableSelectHiddenDropdown'
    },

    showDropdown() {
      this.variant = 'queryableSelect'
    },

    handleValueSelection(selectedValue) {
      this.isDropdownOpen = false

      if (this.shouldAddCustomOption(selectedValue)) {
        this.addCustomOption(selectedValue)
      }

      this.$emit('input', selectedValue)
    },

    shouldAddCustomOption(selectedValue) {
      return both(
        () => this.isCustomOptionShown,
        () => equals(selectedValue, this.inputText)
      )()
    },

    addCustomOption(selectedValue) {
      const customOption = this.createCustomOption(selectedValue)

      if (not(this.hasCustomOption(selectedValue))) {
        this.customOptions.push(customOption)
      }
    },

    createCustomOption(value) {
      return {
        [this.valueAttribute]: value,
        [this.textAttribute]: value,
        isCustom: true
      }
    },

    hasCustomOption(value) {
      return any(
        option => equals(prop(this.valueAttribute, option), value),
        this.customOptions
      )
    },

    hasMatchingOption() {
      return any(this.optionMatchesInput, this.allAvailableOptions)
    },

    optionMatchesInput(option) {
      const optionText = propOr(
        prop(this.valueAttribute, option),
        this.textAttribute,
        option
      )
      return equals(optionText, this.inputText)
    },

    async handleSelectFocus() {
      this.prepareDropdownOpen()
      await this.renderDropdown()
      this.showDropdown()
    },

    prepareDropdownOpen() {
      this.buttonWrapper.classList.add('h-0')
      this.$refs.richSelect.query = this.inputText
      this.isDropdownOpen = true
    },

    async renderDropdown() {
      await this.$nextTick()
    },

    handleSelectBlur() {
      this.$emit('blur')
      this.buttonWrapper.classList.remove('h-0')
      this.isDropdownOpen = false
    },

    isOptionActive(option) {
      return equals(prop(this.valueAttribute, option), this.selectedValue)
    },

    handleKeyDown() {
      if (not(this.isOptionsListHidden())) return

      this.showDropdown()
      this.$refs.richSelect.highlighted = 0
    },

    isOptionsListHidden() {
      return this.$refs.richSelect.$refs.optionsList?.classList.contains('hidden')
    },

    processQuery(query) {
      this.inputText = propOr('', 'length', query) ? query : ''
      return query
    },

    async filterOptions(query) {
      this.processQuery(query)

      const filteredOptions = this.getFilteredOptions(query)

      await this.updateDropdownState(filteredOptions)
      this.forceShowDropdownIfNeeded(filteredOptions)
    },

    getFilteredOptions(query) {
      const baseOptions = this.getBaseFilteredOptions(query)

      return this.shouldAddCustomOptionToResults(query) ?
        this.addCustomOptionToResults(query, baseOptions) :
        baseOptions
    },

    getBaseFilteredOptions(query) {
      const optionsCopy = clone(this.$refs.richSelect.normalizedOptions)

      return query ?
        this.filterOptionsByQuery(query, optionsCopy) :
        optionsCopy
    },

    filterOptionsByQuery(query, options) {
      const term = sanitizeString(query.toLowerCase())
      const filterByText = ({ text, raw }) => {
        const textMatch = sanitizeString(text.toLowerCase()).includes(term)
        const subtitleMatch = raw?.subtitle ? sanitizeString(raw.subtitle.toLowerCase()).includes(term) : false
        return textMatch || subtitleMatch
      }
      return filter(filterByText, options)
    },

    shouldAddCustomOptionToResults(query) {
      return both(
        () => this.allowCustomOption,
        () => both(() => query, () => this.isCustomOptionShown)()
      )()
    },

    addCustomOptionToResults(query, options) {
      const customOption = this.createFilterCustomOption(query)
      return prepend(customOption, options)
    },

    createFilterCustomOption(query) {
      return {
        text: query,
        value: query,
        raw: {
          [this.valueAttribute]: query,
          [this.textAttribute]: query,
          isCustom: true
        }
      }
    },

    async updateDropdownState(filteredOptions) {
      await this.$nextTick()
      this.$refs.richSelect.highlighted = isEmpty(filteredOptions) ? null : 0
      this.$refs.richSelect.filteredOptions = filteredOptions
    },

    forceShowDropdownIfNeeded(filteredOptions) {
      if (this.shouldForceShowDropdown(filteredOptions)) {
        this.showDropdown()
      }
    },

    shouldForceShowDropdown(filteredOptions) {
      return both(
        () => filteredOptions.length > 0,
        () => this.$refs.richSelect.$el.contains(document.activeElement)
      )()
    },

    focusOnQueryableSelect() {
      if (this.selectedValue) return

      this.$refs.richSelect.focus()
    }
  },

  mounted() {
    this.$refs.richSelect.filterOptions = this.filterOptions
    if (this.shouldFocusOnMount) this.focusOnQueryableSelect()
  }
}
</script>
